import { useApolloClient } from '@apollo/client'
import {
  ConfiguracoesIceServersDocument,
  useNotificarEntradaParticipanteVideochamadaMutation,
} from 'graphql/hooks.generated'
import { ConfiguracoesIceServersQuery, ConfiguracoesIceServersQueryVariables } from 'graphql/types.generated'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { useConnectionHeartbeat } from 'hooks/connection/useConnectionHeartbeat'
import useAtmosphere from 'hooks/useAtmosphere'
import { useLogger } from 'hooks/useLogger'
import { useNavigatorOnline } from 'hooks/useNavigatorOnline'
import { useOnBeforeUnload } from 'hooks/useOnBeforeUnload'
import { useServerTime } from 'hooks/useServerTime'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { emptyArrayOf } from 'util/array'

import {
  CallState,
  ChatMessage,
  LocalVideocallParticipant,
  MediaStreamTrackKind,
  RemoteVideocallParticipant,
  VideocallPeerData,
  WebRtcByePayload,
} from '../model-videochamada'
import { createWebRtcPeer, WebRtcPeer } from '../types/WebRtcPeer'
import { getRemoteDescription, isPeerInitiator } from '../utils-webrtc'
import { usePeerReconnection } from './usePeerReconnection'
import { enableTrackStreamEvent } from './useUserMedia'
import { SendSignalCallback, useWebRtcSignalingServer, WebRtcSignal } from './useWebRtcSignalingServer'

interface RemoteParticipantsOptions {
  selfData: LocalVideocallParticipant
  roomId: ID
  audioEnabled: boolean
  videoEnabled: boolean
  presentationStream?: MediaStream
  localStream?: MediaStream
  onPeerDisconnected?(peerId: ID): void
  onNewRemoteMessage?(newMessage: ChatMessage): void
  onConnectionFail?(error: Error): void
  onParticipantJoin?(participant: VideocallPeerData): void
  onParticipantLeave?(participant: RemoteVideocallParticipant): void
}

interface WebRtcChatMessage {
  type: 'chat'
  senderName: string
  message: string
}

interface WebRtcPresentationsMessage {
  type: 'presentations'
  action: 'add' | 'set'
  presentingStreamsIds: ReadonlyArray<ID>
}

interface WebRtcTrackMessage {
  type: 'track'
  kind: MediaStreamTrackKind
  peerId: ID
  audioEnabled?: boolean
  videoEnabled?: boolean
}

type WebRtcDataMessage = WebRtcChatMessage | WebRtcPresentationsMessage | WebRtcTrackMessage | WebRtcByePayload

const updateAudioTracks = (stream: MediaStream, enabled: boolean) => {
  stream.getAudioTracks().forEach((track) => {
    track.enabled = enabled
    stream.dispatchEvent(enableTrackStreamEvent(track))
  })
}

const updateVideoTracks = (stream: MediaStream, enabled: boolean) => {
  stream.getVideoTracks().forEach((track) => {
    track.enabled = enabled
    stream.dispatchEvent(enableTrackStreamEvent(track))
  })
}

const updateTracks = (stream: MediaStream, audioEnabled: boolean, videoEnabled: boolean) => {
  updateAudioTracks(stream, audioEnabled)
  updateVideoTracks(stream, videoEnabled)
}

const LOCAL_MEDIA_READY_TIMEOUT_MS = 5_000 // 5 segundos
const SELF_RECONNECT_WINDOW_MS = 60_000 // 1 minuto

const RECONNECT_OFFER_RETRY_MS = 5_000 // 5 segundos
const RECONNECT_OFFER_MAX_RETRIES = 4
const ANNOUNCE_DEDUPE_MS = 3_000 // 3 segundos
const PEER_HANDSHAKE_GRACE_MS = 10_000 // 10 segundos

export function useWebRtc(props: RemoteParticipantsOptions) {
  const {
    selfData,
    roomId,
    audioEnabled = false,
    videoEnabled = false,
    localStream,
    presentationStream,
    onPeerDisconnected,
    onNewRemoteMessage,
    onConnectionFail,
    onParticipantJoin,
    onParticipantLeave,
  } = props
  const { analytics } = useAnalytics()
  const logger = useLogger()

  const { getServerTimeNow } = useServerTime()
  const client = useApolloClient()
  const iceServersConfiguration = useMemo(
    () =>
      client
        .query<ConfiguracoesIceServersQuery, ConfiguracoesIceServersQueryVariables>({
          query: ConfiguracoesIceServersDocument,
          variables: { input: { userCpf: selfData.cpf } },
        })
        .then((response) => response.data.iceServersConfiguration),
    [client, selfData.cpf]
  )

  const [messages, addMessage] = useReducer(messagesReducer, [])
  const [presentingStreamsIds, dispatchPresenPresentingStreamsIds] = useReducer(
    presentingStreamsIdsReducer,
    new Set<ID>()
  )

  // Tratamento necessário para receber os valores atualizados do state dentro de um useEffect https://legacy.reactjs.org/docs/hooks-faq.html#why-am-i-seeing-stale-props-or-state-inside-my-function
  const presentingStreamsIdsRef = useRef<Set<ID>>(new Set())

  const [remoteParticipants, setRemoteParticipants] = useState<RemoteVideocallParticipant[]>([])
  const [localStreams, setLocalStreams] = useState<ReadonlyArray<MediaStream>>(emptyArrayOf<MediaStream>())
  const [callState, setCallState] = useState<CallState>(CallState.CONNECTING)
  const [hasConnectedOnce, setHasConnectedOnce] = useState(false)

  const peers = useRef<Map<string, WebRtcPeer<WebRtcDataMessage>>>(new Map())
  const remoteParticipantsMap = useRef<Map<string, RemoteVideocallParticipant>>(new Map())
  const knownPeerIds = useRef<Set<ID>>(new Set())
  const peerCreatedAt = useRef<Map<ID, number>>(new Map())
  const lastAnnounceTimestamp = useRef(0)
  const pendingJoinNotification = useRef<Set<ID>>(new Set())
  const signalingServerConnected = useRef(false)
  const participantesTopicConnected = useRef(false)
  const selfGiveUpTimer = useRef<ReturnType<typeof setTimeout>>()
  const isSelfConnectionDownRef = useRef(false)
  const reconnectPeerRef = useRef<(peerId: ID) => void>()
  const abandonStalledPeerRef = useRef<(peerId: ID) => void>()
  const connectRetry = useRef<Map<ID, { timer: ReturnType<typeof setTimeout>; attempts: number }>>(new Map())
  // Guarda o último estado de track recebido por peer para lidar com a condição de corrida
  // em que a mensagem 'track' chega pelo canal de dados antes das tracks de mídia chegarem.
  const lastKnownTrackState = useRef<Map<ID, WebRtcTrackMessage>>(new Map())

  const [notifyNewParticipant] = useNotificarEntradaParticipanteVideochamadaMutation()

  const clearConnectRetry = useCallback((peerId: ID) => {
    const entry = connectRetry.current.get(peerId)
    if (entry) clearTimeout(entry.timer)
    connectRetry.current.delete(peerId)
  }, [])

  const armConnectRetry = useCallback(
    (peerId: ID) => {
      const prev = connectRetry.current.get(peerId)
      if (prev) clearTimeout(prev.timer)
      const attempts = prev?.attempts ?? 0
      const timer = setTimeout(() => {
        const peer = peers.current.get(peerId)
        if (peer && !peer.destroyed) {
          if (peer.connected) return clearConnectRetry(peerId)
          const answerReceived = !!getRemoteDescription(peer)
          if (answerReceived) return clearConnectRetry(peerId)
        }
        if (!remoteParticipantsMap.current.has(peerId)) return clearConnectRetry(peerId)
        // Enquanto a conexão local está caída não adianta contar tentativa nem desistir do peer:
        // quem manda no prazo é a janela de reconexão própria (SELF_RECONNECT_WINDOW_MS)
        const isSelfDown = isSelfConnectionDownRef.current
        if (!isSelfDown && attempts >= RECONNECT_OFFER_MAX_RETRIES) {
          clearConnectRetry(peerId)
          abandonStalledPeerRef.current?.(peerId)
          return
        }
        connectRetry.current.set(peerId, { timer, attempts: isSelfDown ? attempts : attempts + 1 })
        reconnectPeerRef.current?.(peerId)
      }, RECONNECT_OFFER_RETRY_MS)
      connectRetry.current.set(peerId, { timer, attempts })
    },
    [clearConnectRetry]
  )

  const handleIncomingTrack = useCallback(
    (participant: VideocallPeerData, stream: MediaStream, track: MediaStreamTrack) => {
      const currentRemoteParticipant = remoteParticipantsMap.current.get(participant.id)
      if (!currentRemoteParticipant) return
      if (!currentRemoteParticipant.streams.has(stream.id)) currentRemoteParticipant.streams.set(stream.id, stream)
      const currentStream = currentRemoteParticipant.streams.get(stream.id) ?? stream

      if (track.kind === MediaStreamTrackKind.AUDIO)
        currentStream.getAudioTracks().forEach((t) => currentStream.removeTrack(t))
      else if (track.kind === MediaStreamTrackKind.VIDEO)
        currentStream.getVideoTracks().forEach((t) => currentStream.removeTrack(t))

      currentStream.addTrack(track)
      currentStream.dispatchEvent(enableTrackStreamEvent(track))

      const knownState = lastKnownTrackState.current.get(participant.id)
      if (knownState && !presentingStreamsIdsRef.current.has(currentStream.id)) {
        updateTracks(currentStream, knownState.audioEnabled ?? true, knownState.videoEnabled ?? true)
      }

      remoteParticipantsMap.current.set(participant.id, currentRemoteParticipant)

      track.addEventListener('mute', () => {
        if (!presentingStreamsIdsRef.current.has(currentStream.id)) {
          currentStream.removeTrack(track)
          currentStream.dispatchEvent(enableTrackStreamEvent(null))
        }
        setRemoteParticipants([...remoteParticipantsMap.current.values()])
      })
      track.addEventListener('unmute', () => {
        currentStream.addTrack(track)
        currentStream.dispatchEvent(enableTrackStreamEvent(track))
        currentRemoteParticipant.streams.set(currentStream.id, currentStream)
        setRemoteParticipants([...remoteParticipantsMap.current.values()])
      })

      setRemoteParticipants([...remoteParticipantsMap.current.values()])
    },
    []
  )

  useEffect(() => {
    presentingStreamsIdsRef.current = presentingStreamsIds
  }, [presentingStreamsIds])

  const removeParticipant = useCallback(
    (peerId: ID) => {
      logger.info('Removendo participante (saída ou reconexão não concluída)')
      clearConnectRetry(peerId)
      const peer = peers.current.get(peerId)
      const participant = remoteParticipantsMap.current.get(peerId)
      remoteParticipantsMap.current.delete(peerId)
      peers.current.delete(peerId)
      lastKnownTrackState.current.delete(peerId)
      peerCreatedAt.current.delete(peerId)
      if (peer && !peer.destroyed) peer.destroy()
      setRemoteParticipants([...remoteParticipantsMap.current.values()])
      onPeerDisconnected?.(peerId)
      if (participant) onParticipantLeave?.(participant)
    },
    [onPeerDisconnected, onParticipantLeave, logger, clearConnectRetry]
  )

  const isReconnectInitiator = useCallback((peerId: ID) => selfData.id > peerId, [selfData.id])

  const {
    handleIceState,
    hasReconnectingPeers,
    isPeerReconnecting,
    setGiveUpSuspended,
    cancelReconnection,
    clearAll: clearReconnect,
  } = usePeerReconnection({
    isInitiator: isReconnectInitiator,
    onReconnectAttempt: useCallback((peerId: ID) => reconnectPeerRef.current?.(peerId), []),
    onGiveUp: removeParticipant,
  })

  const handleRemoteBye = useCallback(
    (peerId: ID) => {
      logger.info('Peer sinalizou saída (bye)')
      knownPeerIds.current.delete(peerId)
      cancelReconnection(peerId)
      removeParticipant(peerId)
    },
    [logger, cancelReconnection, removeParticipant]
  )

  const abandonStalledPeer = useCallback(
    (peerId: ID) => {
      logger.info('Peer não respondeu às tentativas de conexão, removendo participante')
      cancelReconnection(peerId)
      removeParticipant(peerId)
    },
    [logger, cancelReconnection, removeParticipant]
  )

  useEffect(() => {
    abandonStalledPeerRef.current = abandonStalledPeer
  }, [abandonStalledPeer])

  const discardStalePeer = useCallback(
    (peerId: ID) => {
      const existing = peers.current.get(peerId)
      if (existing && !existing.destroyed && (!existing.connected || isPeerReconnecting(peerId))) {
        peers.current.delete(peerId)
        existing.destroy()
      }
    },
    [isPeerReconnecting]
  )

  const handlePeerClosed = useCallback(
    (closedPeer: WebRtcPeer<WebRtcDataMessage>, participant: VideocallPeerData) => {
      if (peers.current.get(participant.id) !== closedPeer) return
      logger.info('Conexão com Peer fechada')
      peers.current.delete(participant.id)
      if (!remoteParticipantsMap.current.has(participant.id)) return
      handleIceState(participant.id, 'failed')
    },
    [handleIceState, logger]
  )

  const handleData = useCallback(
    (data: WebRtcDataMessage) => {
      switch (data.type) {
        case 'chat': {
          const message = {
            sender: data.senderName,
            time: getServerTimeNow().getTime(),
            content: data.message,
            local: false,
          }
          addMessage(message)
          onNewRemoteMessage?.(message)
          break
        }
        case 'bye': {
          handleRemoteBye(data.peerId)
          break
        }
        case 'presentations':
          data.presentingStreamsIds.length > 0 && logger.info('Recebendo compartilhamento de tela')
          if (data.action === 'add')
            data.presentingStreamsIds.forEach((streamId) =>
              dispatchPresenPresentingStreamsIds({
                type: 'add',
                streamId,
                peers: peers.current,
              })
            )
          else if (data.action === 'set')
            dispatchPresenPresentingStreamsIds({
              type: 'set',
              streamIds: data.presentingStreamsIds,
            })
          break
        case 'track': {
          lastKnownTrackState.current.set(data.peerId, data)
          const remoteStreams = remoteParticipantsMap.current.get(data.peerId)?.streams
          remoteStreams?.forEach((stream) => {
            if (!presentingStreamsIdsRef.current.has(stream.id)) {
              if (data.kind === MediaStreamTrackKind.BOTH)
                updateTracks(stream, data.audioEnabled ?? true, data.videoEnabled ?? true)
              else if (data.kind === MediaStreamTrackKind.AUDIO) updateAudioTracks(stream, data.audioEnabled ?? true)
              else updateVideoTracks(stream, data.videoEnabled ?? true)
            }
          })
          setRemoteParticipants([...remoteParticipantsMap.current.values()])
        }
      }
    },
    [getServerTimeNow, onNewRemoteMessage, logger, handleRemoteBye]
  )

  const addPresentingStreamId = useCallback(
    (streamId: ID) => dispatchPresenPresentingStreamsIds({ type: 'add', streamId, peers: peers.current }),
    []
  )

  const removePresentingStreamId = useCallback(
    (streamId: ID) => dispatchPresenPresentingStreamsIds({ type: 'remove', streamId, peers: peers.current }),
    []
  )

  const sendMessage = useCallback(
    (message: string) => {
      peers.current.forEach((peer) => {
        peer.sendData({ type: 'chat', senderName: selfData.nome, message })
      })
      addMessage({
        sender: selfData.nome,
        time: getServerTimeNow().getTime(),
        content: message,
        local: true,
      })
    },
    [getServerTimeNow, selfData.nome]
  )

  const audioEnabledRef = useRef(audioEnabled)
  const videoEnabledRef = useRef(videoEnabled)

  const localStreamsRef = useRef<ReadonlyArray<MediaStream>>(localStreams)
  useEffect(() => {
    localStreamsRef.current = localStreams
  }, [localStreams])

  const mediaReady = useRef<{ promise: Promise<void>; resolve: () => void }>()
  if (!mediaReady.current) {
    let resolveReady: () => void
    const promise = new Promise<void>((resolve) => {
      resolveReady = resolve
    })
    mediaReady.current = { promise, resolve: resolveReady! }
  }

  useEffect(() => {
    const intendsToSendMedia = audioEnabled || videoEnabled
    if (localStream || !intendsToSendMedia) mediaReady.current!.resolve()
  }, [audioEnabled, videoEnabled, localStream])

  useEffect(() => {
    const timer = setTimeout(() => mediaReady.current!.resolve(), LOCAL_MEDIA_READY_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [])

  const handleConnect = useCallback(
    async (peer: WebRtcPeer<WebRtcDataMessage>, participant: VideocallPeerData) => {
      logger.info('Peer conectado')
      clearConnectRetry(participant.id)
      setHasConnectedOnce(true)
      peer.sendData({ type: 'presentations', action: 'add', presentingStreamsIds: [...presentingStreamsIds] })
      peer.sendData({
        type: 'track',
        kind: MediaStreamTrackKind.BOTH,
        peerId: selfData.id,
        audioEnabled: audioEnabledRef.current,
        videoEnabled: videoEnabledRef.current,
      })

      if (pendingJoinNotification.current.delete(participant.id)) onParticipantJoin?.(participant)

      if (await peer.isUsingRelay()) analytics.logEvent('tele_videochamadas_conexao_com_turn')
      else analytics.logEvent('tele_videochamadas_conexao_sem_turn')
    },
    [analytics, presentingStreamsIds, logger, selfData.id, onParticipantJoin, clearConnectRetry]
  )

  const addNewPeer = useCallback(
    async (participant: VideocallPeerData, initiator: boolean, sendSignal: SendSignalCallback) => {
      if (
        selfData.id !== participant.id &&
        (!peers.current.has(participant.id) || peers.current.get(participant.id)?.destroyed)
      ) {
        try {
          await mediaReady.current!.promise

          const existing = peers.current.get(participant.id)
          if (existing && !existing.destroyed) return

          const { urls, user, password } = await iceServersConfiguration

          const newPeer = createWebRtcPeer<WebRtcDataMessage>({
            initiator,
            trickle: false,
            config: {
              iceServers: [
                {
                  urls: [...urls],
                  username: user ?? undefined,
                  credential: password ?? undefined,
                },
              ],
            },
            onSignal: (signal) => {
              sendSignal(participant.id, signal)
              if (initiator && signal.type === 'offer' && !newPeer.connected) armConnectRetry(participant.id)
            },
            onTrack: (track, stream) => handleIncomingTrack(participant, stream, track),
            onClose: () => handlePeerClosed(newPeer, participant),
            onConnect: () => void handleConnect(newPeer, participant),
            onData: handleData,
            onIceStateChange: (state) => handleIceState(participant.id, state),
            onError: (_error, peer) => peer?.destroy(),
          })

          remoteParticipantsMap.current.set(participant.id, {
            id: participant.id,
            streams: new Map<ID, MediaStream>(),
            nome: participant.nome,
          })
          setRemoteParticipants([...remoteParticipantsMap.current.values()])
          peers.current.set(participant.id, newPeer)
          knownPeerIds.current.add(participant.id)
          peerCreatedAt.current.set(participant.id, Date.now())

          localStreamsRef.current.forEach((stream: MediaStream) => newPeer.addStream(stream))
        } catch (err) {
          onConnectionFail?.(err as Error)
        }
      }
    },
    [
      selfData.id,
      iceServersConfiguration,
      handleData,
      handleIncomingTrack,
      handlePeerClosed,
      handleConnect,
      handleIceState,
      onConnectionFail,
      armConnectRetry,
    ]
  )

  const handleSignal = useCallback(
    async ({ sender, data }: WebRtcSignal, sendSignal: SendSignalCallback) => {
      logger.info('Sinal de peer recebido')
      if (data.type === 'bye') {
        handleRemoteBye(sender.id)
        return
      }
      if (data.type === 'offer') {
        const existing = peers.current.get(sender.id)
        const isLocalOfferPending =
          !!existing && !existing.destroyed && !existing.connected && isPeerInitiator(existing)
        if (isLocalOfferPending && isReconnectInitiator(sender.id)) {
          return
        }
        discardStalePeer(sender.id)
      }
      await addNewPeer(sender, false, sendSignal)
      peers.current.get(sender.id)?.signal(data)
    },
    [addNewPeer, discardStalePeer, logger, isReconnectInitiator, handleRemoteBye]
  )

  const announceSelf = useCallback(() => {
    const now = Date.now()
    if (now - lastAnnounceTimestamp.current < ANNOUNCE_DEDUPE_MS) return
    lastAnnounceTimestamp.current = now
    notifyNewParticipant({
      variables: { videochamadaUuid: roomId, participanteId: selfData.id, nomeParticipante: selfData.nome },
    }).catch((error) => {
      lastAnnounceTimestamp.current = 0
      logger.error('Falha ao anunciar participante na videochamada', error)
    })
  }, [notifyNewParticipant, roomId, selfData.id, selfData.nome, logger])

  const handleTopicConnected = useCallback(
    () => participantesTopicConnected.current && signalingServerConnected.current && announceSelf(),
    [announceSelf]
  )

  const handleSignalingServerConnected = useCallback(() => {
    logger.info('Servidor de sinalização entre peers conectado')
    signalingServerConnected.current = true
    handleTopicConnected()
  }, [handleTopicConnected, logger])

  const handleParticipantesTopicConnected = useCallback(() => {
    logger.info('Topico de peers conectado')
    participantesTopicConnected.current = true
    handleTopicConnected()
  }, [handleTopicConnected, logger])

  const { sendSignal } = useWebRtcSignalingServer({
    selfData,
    roomId,
    onSignal: (signal, sendSignalCallback) => void handleSignal(signal, sendSignalCallback),
    onConnect: handleSignalingServerConnected,
  })

  const reconnectPeer = useCallback(
    (peerId: ID) => {
      const participant = remoteParticipantsMap.current.get(peerId)
      if (!participant) return
      const existing = peers.current.get(peerId)
      if (existing && !existing.destroyed && existing.connected) return
      peers.current.delete(peerId)
      if (existing && !existing.destroyed) existing.destroy()
      addNewPeer({ id: participant.id, nome: participant.nome }, true, sendSignal)
    },
    [addNewPeer, sendSignal]
  )

  useEffect(() => {
    reconnectPeerRef.current = reconnectPeer
  }, [reconnectPeer])

  const navigatorOnline = useNavigatorOnline()

  // O heartbeat continua ligado enquanto o servidor não responder, mesmo depois do evento 'online':
  // sem isso o navigator.onLine oscilando (wi-fi que reassocia sem internet) zeraria a janela de
  // reconexão de 1 minuto a cada oscilação, antes de qualquer probe confirmar o servidor
  const isServerReachableRef = useRef(true)
  const { isServerReachable } = useConnectionHeartbeat({
    enabled: hasReconnectingPeers || !navigatorOnline || !isServerReachableRef.current,
  })
  isServerReachableRef.current = isServerReachable

  const isSelfConnectionDown = !navigatorOnline || !isServerReachable

  useEffect(() => {
    isSelfConnectionDownRef.current = isSelfConnectionDown
    setGiveUpSuspended(isSelfConnectionDown)
  }, [isSelfConnectionDown, setGiveUpSuspended])

  useEffect(() => {
    if (isSelfConnectionDown) {
      setCallState((prev) => (prev === CallState.DISCONNECTED ? prev : CallState.RECONNECTING))
      if (!selfGiveUpTimer.current) {
        selfGiveUpTimer.current = setTimeout(() => setCallState(CallState.DISCONNECTED), SELF_RECONNECT_WINDOW_MS)
      }
    } else {
      if (selfGiveUpTimer.current) {
        clearTimeout(selfGiveUpTimer.current)
        selfGiveUpTimer.current = undefined
      }
      setCallState((prev) => {
        if (prev === CallState.DISCONNECTED) return prev
        return hasConnectedOnce ? CallState.CONNECTED : CallState.CONNECTING
      })
    }
  }, [isSelfConnectionDown, hasConnectedOnce])

  const prevCallState = useRef(callState)
  useEffect(() => {
    if (prevCallState.current === CallState.RECONNECTING && callState === CallState.CONNECTED) {
      logger.info('Conectividade recuperada, reanunciando participante')
      announceSelf()
    }
    prevCallState.current = callState
  }, [callState, logger, announceSelf])

  const handleNewParticipant = useCallback(
    (newParticipant: LocalVideocallParticipant) => {
      logger.info('Peer entrou na videochamada: ' + newParticipant.nome)
      const existing = peers.current.get(newParticipant.id)
      const isConnectedToPeer = !!existing && !existing.destroyed && existing.connected
      if (!isConnectedToPeer) pendingJoinNotification.current.add(newParticipant.id)
      const isHandshakeInProgress =
        !!existing &&
        !existing.destroyed &&
        !existing.connected &&
        Date.now() - (peerCreatedAt.current.get(newParticipant.id) ?? 0) < PEER_HANDSHAKE_GRACE_MS
      if (isHandshakeInProgress) return
      discardStalePeer(newParticipant.id)
      addNewPeer(newParticipant, true, sendSignal)
    },
    [addNewPeer, discardStalePeer, sendSignal, logger]
  )

  useAtmosphere<LocalVideocallParticipant>({
    topic: `public/videochamada/${roomId}/participantes`,
    onMessage: handleNewParticipant,
    onConnect: handleParticipantesTopicConnected,
    reconnectPolicy: 'retryForever',
  })

  const lastAudioTrackEnabled = useRef<boolean>()
  const lastVideoTrackEnabled = useRef<boolean>()
  const changedLocalStreams = useRef<ReadonlyArray<MediaStream>>()

  useEffect(() => {
    audioEnabledRef.current = audioEnabled
    videoEnabledRef.current = videoEnabled

    const localStreamsCurrent = [
      ...(presentationStream ? [presentationStream] : emptyArrayOf<MediaStream>()),
      ...(localStream ? [localStream] : emptyArrayOf<MediaStream>()),
    ]

    const prevStreams = changedLocalStreams.current ?? emptyArrayOf<MediaStream>()
    const streamsChanged =
      localStreamsCurrent.length !== prevStreams.length ||
      localStreamsCurrent.some((stream, i) => stream.id !== prevStreams[i]?.id)

    if (streamsChanged) {
      setLocalStreams(localStreamsCurrent)
      changedLocalStreams.current = localStreamsCurrent
    }

    if (localStream) {
      const audioChanged = lastAudioTrackEnabled.current !== audioEnabled
      const videoChanged = lastVideoTrackEnabled.current !== videoEnabled

      if (audioChanged || videoChanged) {
        let kind: MediaStreamTrackKind
        if (audioChanged && videoChanged) kind = MediaStreamTrackKind.BOTH
        else if (audioChanged) kind = MediaStreamTrackKind.AUDIO
        else kind = MediaStreamTrackKind.VIDEO

        peers.current.forEach((peer) => {
          if (!peer.destroyed) {
            peer.sendData({
              type: 'track',
              kind: kind,
              peerId: selfData.id,
              audioEnabled: audioEnabled,
              videoEnabled: videoEnabled,
            })
          }
        })
      }
      lastAudioTrackEnabled.current = audioEnabled
      lastVideoTrackEnabled.current = videoEnabled
    }
  }, [audioEnabled, localStream, presentationStream, selfData.id, videoEnabled])

  const lastLocalStreams = useRef<ReadonlyArray<MediaStream>>(emptyArrayOf<MediaStream>())
  useEffect(() => {
    const streamsAdd =
      localStreams?.filter((stream) => !lastLocalStreams.current.some((prevStream) => prevStream.id === stream.id)) ??
      []
    const streamsRemove =
      lastLocalStreams.current?.filter((prevStream) => !localStreams.some((stream) => prevStream.id === stream.id)) ??
      []

    peers.current.forEach((peer) => {
      if (!peer.destroyed) {
        streamsRemove.forEach((stream) => peer.removeStream(stream))
        streamsAdd.forEach((stream) => peer.addStream(stream))
      }
    })
    lastLocalStreams.current = localStreams
  }, [localStreams])

  const lastTrackStream = useRef<MediaStream>()
  const lastLocalAudioTrack = useRef<MediaStreamTrack>()
  const lastLocalVideoTrack = useRef<MediaStreamTrack>()
  const localAudioTrack = localStream?.getAudioTracks()[0]
  const localVideoTrack = localStream?.getVideoTracks()[0]

  useEffect(() => {
    if (!localStream) {
      lastTrackStream.current = undefined
      lastLocalAudioTrack.current = undefined
      lastLocalVideoTrack.current = undefined
      return
    }

    if (lastTrackStream.current !== localStream) {
      lastTrackStream.current = localStream
      lastLocalAudioTrack.current = localAudioTrack
      lastLocalVideoTrack.current = localVideoTrack
      return
    }

    const previousAudioTrack = lastLocalAudioTrack.current
    const previousVideoTrack = lastLocalVideoTrack.current

    peers.current.forEach((peer: WebRtcPeer<WebRtcDataMessage>) => {
      if (peer.destroyed) return

      if (previousAudioTrack && localAudioTrack && previousAudioTrack !== localAudioTrack)
        peer.replaceTrack?.(previousAudioTrack, localAudioTrack, localStream)

      if (previousVideoTrack && localVideoTrack && previousVideoTrack !== localVideoTrack)
        peer.replaceTrack?.(previousVideoTrack, localVideoTrack, localStream)
    })

    lastLocalAudioTrack.current = localAudioTrack
    lastLocalVideoTrack.current = localVideoTrack
  }, [localStream, localAudioTrack, localVideoTrack])

  const notifyLeaving = useCallback(() => {
    peers.current.forEach((peer) => {
      if (!peer.destroyed) {
        peer.sendData({ type: 'bye', peerId: selfData.id })
      }
    })
    knownPeerIds.current.forEach((peerId) => {
      sendSignal(peerId, { type: 'bye', peerId: selfData.id })
    })
  }, [selfData.id, sendSignal])

  const destroyAllPeers = useCallback(() => {
    notifyLeaving()
    const allPeers = [...peers.current.values()]
    peers.current.clear()
    remoteParticipantsMap.current.clear()
    knownPeerIds.current.clear()
    peerCreatedAt.current.clear()
    pendingJoinNotification.current.clear()
    lastKnownTrackState.current.clear()
    allPeers.forEach((peer) => peer && !peer.destroyed && peer.destroy())
    connectRetry.current.forEach((entry) => clearTimeout(entry.timer))
    connectRetry.current.clear()
    clearReconnect()
    if (selfGiveUpTimer.current) {
      clearTimeout(selfGiveUpTimer.current)
      selfGiveUpTimer.current = undefined
    }
  }, [clearReconnect, notifyLeaving])
  const destroyAllPeersRef = useRef(destroyAllPeers)
  useEffect(() => {
    destroyAllPeersRef.current = destroyAllPeers
  }, [destroyAllPeers])
  //onDestroy
  useEffect(() => () => destroyAllPeersRef.current(), [])
  useOnBeforeUnload(destroyAllPeers)

  return {
    remoteParticipants,
    sendMessage,
    messages,
    addPresentingStreamId,
    removePresentingStreamId,
    presentingStreamsIds: presentingStreamsIds as ReadonlySet<ID>,
    callState,
    notifyLeaving,
  }
}

const messagesReducer = (prevMessages: ChatMessage[], newMessage: ChatMessage) => [...prevMessages, newMessage]

const presentingStreamsIdsReducer = (
  prevPresentingStreamsIds: Set<ID>,
  action:
    | { type: 'add' | 'remove'; streamId: ID; peers: Map<string, WebRtcPeer<WebRtcDataMessage>> }
    | { type: 'set'; streamIds: ReadonlyArray<ID> }
) => {
  switch (action.type) {
    case 'add': {
      if (prevPresentingStreamsIds.has(action.streamId)) return prevPresentingStreamsIds
      const next = new Set(prevPresentingStreamsIds)
      next.add(action.streamId)
      action.peers.forEach((peer) => {
        peer.sendData({ type: 'presentations', action: 'set', presentingStreamsIds: [...next] })
      })
      return next
    }
    case 'remove': {
      if (!prevPresentingStreamsIds.has(action.streamId)) return prevPresentingStreamsIds
      const next = new Set(prevPresentingStreamsIds)
      next.delete(action.streamId)
      action.peers.forEach((peer) => {
        peer.sendData({ type: 'presentations', action: 'set', presentingStreamsIds: [...next] })
      })
      return next
    }
    case 'set':
      return new Set(action.streamIds)
  }
}

export const WEBRTC_CONNECTION_FAILURE_MESSAGE = 'Connection failed.'
export const WEBRTC_ICE_CONNECTION_FAILURE_MESSAGE = 'Ice connection failed.'
